import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { apiEndpoints } from '../services/api';
import CalendarPicker from './CalendarPicker';
import CustomSelect from './CustomSelect';
import CompanySelect from './CompanySelect';
import AutocompleteInput from './AutocompleteInput';
import { useFormData } from '../hooks/useFormData';

const toInputDate = (str) => {
  if (!str) return '';
  const s = String(str);
  const m1 = s.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (m1) return `${m1[3]}-${m1[2]}-${m1[1]}`;
  const m2 = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m2) return `${m2[1]}-${m2[2]}-${m2[3]}`;
  return '';
};

const emptyDoc = { exp: '', issuer: '', issued: '' };

const DocSection = ({ label, optional, values, onChange, issuers = [] }) => (
  <div style={{ marginBottom: 18 }}>
    <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-2)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {label}
    </div>
    <div className="form-row">
      <div className="form-group">
        <label className="form-label">Expiry Date{!optional && ' *'}</label>
        <CalendarPicker
          name="exp"
          value={values.exp}
          onChange={e => onChange('exp', e.target.value)}
          required={!optional}
        />
      </div>
      <div className="form-group">
        <label className="form-label">Issuer</label>
        <AutocompleteInput
          name="issuer"
          value={values.issuer || ''}
          onChange={e => onChange('issuer', e.target.value)}
          onSelect={val => onChange('issuer', val)}
          suggestions={issuers}
          placeholder="e.g. Allianz, RAR"
        />
      </div>
    </div>
  </div>
);

const VehicleForm = ({ isOpen, onClose, vehicle, onSave, isTemporary = false }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    plate: '', vin: '', brand: '', model: '', year: '', fuel: 'Diesel',
    bodyType: 'Sedan', mileage: '', company: '', color: '', observations: '',
    status: 'Disponibil',
  });
  const [docs, setDocs] = useState({
    rca:           { ...emptyDoc },
    itp:           { ...emptyDoc },
    casco:         { ...emptyDoc },
    rovinieta:     { exp: '' },
    roadAssistance: { issued: '', exp: '' },
  });
  const [driverId, setDriverId] = useState(null);
  const [assignment, setAssignment] = useState({ assignedDate: '', returnDate: '', fuelLevelTaken: '', fuelTimeTaken: '', fuelLevelReturn: '', fuelTimeReturn: '' });
  const [loading, setLoading] = useState(false);
  const [drivers, setDrivers] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    apiEndpoints.drivers.list().then(res => setDrivers(res.data || [])).catch(() => {});
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setCurrentStep(1);
    if (vehicle) {
      setFormData({
        plate:        vehicle.plate || '',
        vin:          vehicle.vin || '',
        brand:        vehicle.brand || '',
        model:        vehicle.model || '',
        year:         vehicle.year || '',
        fuel:         vehicle.fuel || 'Diesel',
        bodyType:     vehicle.bodyType || 'Sedan',
        mileage:      vehicle.mileage || '',
        company:      vehicle.company || '',
        color:        vehicle.color || '',
        observations: vehicle.observations || '',
        status:       vehicle.status || 'Disponibil',
      });
      const cleanIssuer = (v) => (v && v !== '—' ? v : '');
      setDocs({
        rca:           { exp: toInputDate(vehicle.docs?.rca?.exp),   issuer: cleanIssuer(vehicle.docs?.rca?.issuer),   issued: toInputDate(vehicle.docs?.rca?.issued)   },
        itp:           { exp: toInputDate(vehicle.docs?.itp?.exp),   issuer: cleanIssuer(vehicle.docs?.itp?.issuer),   issued: toInputDate(vehicle.docs?.itp?.issued)   },
        casco:         { exp: toInputDate(vehicle.docs?.casco?.exp), issuer: cleanIssuer(vehicle.docs?.casco?.issuer), issued: toInputDate(vehicle.docs?.casco?.issued) },
        rovinieta:     { exp: toInputDate(vehicle.docs?.rovinieta?.exp) },
        roadAssistance: { issued: toInputDate(vehicle.docs?.roadAssistance?.issued), exp: toInputDate(vehicle.docs?.roadAssistance?.exp) },
      });
      setDriverId(vehicle.driverId || null);
      setAssignment({
        assignedDate:    vehicle.assignedDate    || '',
        returnDate:      vehicle.returnDate      || '',
        fuelLevelTaken:  vehicle.fuelLevelTaken  != null ? String(vehicle.fuelLevelTaken) : '',
        fuelTimeTaken:   vehicle.fuelTimeTaken   || '',
        fuelLevelReturn: vehicle.fuelLevelReturn != null ? String(vehicle.fuelLevelReturn) : '',
        fuelTimeReturn:  vehicle.fuelTimeReturn  || '',
      });
    } else {
      setFormData({
        plate: '', vin: '', brand: '', model: '', year: '', fuel: 'Diesel',
        bodyType: 'Sedan', mileage: '', company: '', color: '', observations: '',
        status: 'Disponibil',
      });
      setDocs({ rca: { ...emptyDoc }, itp: { ...emptyDoc }, casco: { ...emptyDoc }, rovinieta: { exp: '' }, roadAssistance: { issued: '', exp: '' } });
      setDriverId(null);
      setAssignment({ assignedDate: '', returnDate: '', fuelLevelTaken: '', fuelTimeTaken: '', fuelLevelReturn: '', fuelTimeReturn: '' });
    }
  }, [vehicle, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDocChange = (type, field, value) => {
    setDocs(prev => ({ ...prev, [type]: { ...prev[type], [field]: value } }));
  };

  const handleNext = () => setCurrentStep(prev => prev + 1);
  const handleBack = () => setCurrentStep(prev => prev - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (currentStep < 3) {
      handleNext();
      return;
    }
    setLoading(true);
    try {
      await onSave({
        ...formData,
        year:     parseInt(formData.year)    || null,
        mileage:  parseInt(formData.mileage) || null,
        docs:     { rca: docs.rca, itp: docs.itp, casco: docs.casco, rovinieta: docs.rovinieta, roadAssistance: docs.roadAssistance },
        driverId: driverId || null,
        assignedDate:    assignment.assignedDate    || '',
        returnDate:      assignment.returnDate      || '',
        fuelLevelTaken:  assignment.fuelLevelTaken  !== '' ? Number(assignment.fuelLevelTaken)  : null,
        fuelLevelReturn: assignment.fuelLevelReturn !== '' ? Number(assignment.fuelLevelReturn) : null,
      });
      onClose();
    } catch (error) {
      console.error('Error saving vehicle:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectedDriver = drivers.find(d => d.id === driverId || d._id === driverId);
  const fd = useFormData();
  const modelSuggestions = formData.brand ? fd.modelsForBrand(formData.brand) : fd.allModels;

  const handlePlateSelect = (plate) => {
    const v = fd.getVehicleByPlate(plate);
    if (!v) return;
    setFormData(prev => ({
      ...prev,
      plate,
      vin:     v.vin     || prev.vin,
      brand:   v.brand   || prev.brand,
      model:   v.model   || prev.model,
      company: v.company || prev.company,
      color:   v.color   || prev.color,
    }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={vehicle ? 'Edit Vehicle' : 'Add New Vehicle'} size="md">
      <div className="stepper-container">
        <form onSubmit={handleSubmit} className="mt-4">
          {/* ── Step 1: Vehicle Data ── */}
          {currentStep === 1 && (
            <div className="step-content">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Registration Number</label>
                  <AutocompleteInput name="plate" value={formData.plate} onChange={handleChange}
                    suggestions={fd.plates} onSelect={handlePlateSelect} placeholder="E.G. B-12-XYZ" />
                </div>
                <div className="form-group">
                  <label className="form-label">VIN / Chassis Number</label>
                  <AutocompleteInput name="vin" value={formData.vin} onChange={handleChange}
                    suggestions={fd.vins} placeholder="e.g. WBA8E9G52HNU..." />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Brand</label>
                  <AutocompleteInput name="brand" value={formData.brand} onChange={handleChange}
                    suggestions={fd.brands} placeholder="e.g. BMW, Mercedes, Dacia" />
                </div>
                <div className="form-group">
                  <label className="form-label">Model</label>
                  <AutocompleteInput name="model" value={formData.model} onChange={handleChange}
                    suggestions={modelSuggestions} placeholder="e.g. Logan, Transit, 3 Series" />
                </div>
              </div>

              <div className="form-row row-three">
                <div className="form-group">
                  <label className="form-label">Year of manufacture</label>
                  <input type="number" name="year" className="form-input" value={formData.year} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label className="form-label">Combustible</label>
                  <CustomSelect name="fuel" className="form-select" value={formData.fuel} onChange={handleChange}>
                    <option value="Diesel">Diesel</option>
                    <option value="Petrol">Petrol</option>
                    <option value="Electric">Electric</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className="form-label">Body type</label>
                  <CustomSelect name="bodyType" className="form-select" value={formData.bodyType} onChange={handleChange}>
                    <option value="Sedan">Sedan</option>
                    <option value="Van">Van</option>
                    <option value="Truck">Truck</option>
                  </CustomSelect>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Company</label>
                  <CompanySelect name="company" value={formData.company} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label className="form-label">Color</label>
                  <AutocompleteInput name="color" value={formData.color} onChange={handleChange}
                    suggestions={fd.colors} placeholder="e.g. White, Black, Grey" />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Observations</label>
                <textarea name="observations" className="form-input" rows="3" value={formData.observations} onChange={handleChange} placeholder="Any relevant information..." />
              </div>
            </div>
          )}

          {/* ── Step 2: Documents & Expirations ── */}
          {currentStep === 2 && (
            <div className="step-content">
              <DocSection
                label="RCA Insurance" optional={isTemporary}
                values={docs.rca} issuers={fd.issuers}
                onChange={(field, val) => handleDocChange('rca', field, val)}
              />
              <DocSection
                label="ITP" optional={isTemporary}
                values={docs.itp} issuers={fd.issuers}
                onChange={(field, val) => handleDocChange('itp', field, val)}
              />

              {/* Rovinieta */}
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-2)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  ROVINIETA
                </div>
                <div className="form-group">
                  <label className="form-label">Expiry Date</label>
                  <CalendarPicker
                    name="rovinietaExp"
                    value={docs.rovinieta.exp}
                    onChange={e => handleDocChange('rovinieta', 'exp', e.target.value)}
                  />
                </div>
              </div>

              <DocSection
                label="CASCO" optional={true}
                values={docs.casco} issuers={fd.issuers}
                onChange={(field, val) => handleDocChange('casco', field, val)}
              />

              {/* Road Assistance */}
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-2)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  ROAD ASSISTANCE
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Issue Date</label>
                    <CalendarPicker
                      name="roadAssistanceIssued"
                      value={docs.roadAssistance.issued}
                      onChange={e => handleDocChange('roadAssistance', 'issued', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Expiration Date</label>
                    <CalendarPicker
                      name="roadAssistanceExp"
                      value={docs.roadAssistance.exp}
                      onChange={e => handleDocChange('roadAssistance', 'exp', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Step 3: Driver Assignment ── */}
          {currentStep === 3 && (
            <div className="step-content">
              <div className="form-group">
                <label className="form-label">Assign Driver</label>
                <CustomSelect
                  name="driverId"
                  className="form-select"
                  value={driverId || ''}
                  onChange={e => setDriverId(e.target.value || null)}
                >
                  <option value="">— No driver assigned —</option>
                  {drivers.map(d => (
                    <option key={d.id || d._id} value={d.id || d._id}>
                      {d.name}
                    </option>
                  ))}
                </CustomSelect>
              </div>

              {selectedDriver ? (
                <div style={{ marginTop: 12, padding: '12px 16px', background: 'var(--bg-2)', borderRadius: 10, border: '1px solid var(--border)' }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{selectedDriver.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 2 }}>{selectedDriver.role || 'Fleet driver'}</div>
                  {selectedDriver.phone && <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 2 }}>{selectedDriver.phone}</div>}
                </div>
              ) : (
                <div style={{ marginTop: 12, padding: '12px 16px', background: 'var(--bg-2)', borderRadius: 10, border: '1px solid var(--border)', color: 'var(--text-2)', fontSize: 13, textAlign: 'center' }}>
                  Vehicle will be saved as <strong>Available</strong>
                </div>
              )}

              <div style={{ marginTop: 20, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>
                  Assignment Details
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Vehicle Taken Date</label>
                    <CalendarPicker name="assignedDate" value={assignment.assignedDate}
                      onChange={e => setAssignment(p => ({ ...p, assignedDate: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Vehicle Return Date</label>
                    <CalendarPicker name="returnDate" value={assignment.returnDate}
                      onChange={e => setAssignment(p => ({ ...p, returnDate: e.target.value }))} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Fuel Level Taken (%)</label>
                    <input type="number" className="form-input" min="0" max="100"
                      value={assignment.fuelLevelTaken}
                      onChange={e => setAssignment(p => ({ ...p, fuelLevelTaken: e.target.value }))}
                      placeholder="e.g. 75" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Fuel Level Return (%)</label>
                    <input type="number" className="form-input" min="0" max="100"
                      value={assignment.fuelLevelReturn}
                      onChange={e => setAssignment(p => ({ ...p, fuelLevelReturn: e.target.value }))}
                      placeholder="e.g. 40" />
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="form-actions-stepper">
            <button type="button" className="btn btn-secondary" onClick={currentStep === 1 ? onClose : handleBack}>
              {currentStep === 1 ? 'Cancel' : 'Back'}
            </button>
            <button type="submit" className="btn btn-primary-dark" disabled={loading}>
              {loading ? 'Saving...' : currentStep === 3 ? 'Finish' : 'Continue →'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default VehicleForm;
